import { appsflyerService } from '../services/appsflyerService';

// Event tracking utilities for AppsFlyer
// These functions provide a convenient API for tracking common user actions

/**
 * Track user onboarding events
 */
export const trackOnboardingEvents = {
  /**
   * Track when user selects motivations during onboarding
   */
  motivationSelected: async (motivation: string, totalSelected: number) => {
    await appsflyerService.trackEvent('onboarding_motivation_selected', {
      motivation,
      total_selected: totalSelected,
    });
  },

  /**
   * Track when user selects symbols during onboarding
   */
  symbolSelected: async (symbol: string, action: 'add' | 'remove', totalSymbols: number) => {
    await appsflyerService.trackEvent('onboarding_symbol_selected', {
      symbol,
      action,
      total_symbols: totalSymbols,
    });
  },

  /**
   * Track onboarding step navigation
   */
  stepNavigation: async (step: number, direction: 'next' | 'back') => {
    await appsflyerService.trackEvent('onboarding_navigation', {
      step,
      direction,
    });
  },

  /**
   * Track onboarding completion
   */
  completed: async (motivations: string[], selectedSymbols: string[], completionTimeMs: number) => {
    await appsflyerService.trackOnboardingComplete(motivations, selectedSymbols);
    await appsflyerService.trackEvent('onboarding_completion_time', {
      completion_time_ms: completionTimeMs,
    });
  },
};

/**
 * Track subscription and revenue events
 */
export const trackSubscriptionEvents = {
  /**
   * Track subscription purchase
   */
  purchased: async (planName: string, price: number, currency = 'USD') => {
    await appsflyerService.trackSubscription(planName, price, currency);
  },

  /**
   * Track trial started
   */
  trialStarted: async (planName: string, trialDurationDays: number, priceAfterTrial: number, currency = 'USD') => {
    await appsflyerService.trackEvent('trial_started', {
      plan_name: planName,
      trial_duration_days: trialDurationDays,
      price_after_trial: priceAfterTrial,
      af_currency: currency,
      trial_start_time: new Date().toISOString(),
    });
  },

  /**
   * Track trial converted to paid subscription
   */
  trialConverted: async (planName: string, subscriptionPrice: number, currency = 'USD') => {
    await appsflyerService.trackEvent('trial_converted', {
      plan_name: planName,
      af_revenue: subscriptionPrice,
      af_currency: currency,
      conversion_date: new Date().toISOString(),
    });
  },

  /**
   * Track subscription cancellation
   */
  cancelled: async (planName: string, reason?: string, daysIntoTrial?: number) => {
    await appsflyerService.trackEvent('subscription_cancelled', {
      plan_name: planName,
      cancellation_reason: reason || 'unknown',
      ...(daysIntoTrial !== undefined && {
        days_into_trial: daysIntoTrial,
        cancelled_during_trial: true,
      }),
    });
  },

  /**
   * Track trial expiration without conversion
   */
  trialExpired: async (planName: string, trialDurationDays: number) => {
    await appsflyerService.trackEvent('trial_expired', {
      plan_name: planName,
      trial_duration_days: trialDurationDays,
      did_not_convert: true,
      expiration_date: new Date().toISOString(),
    });
  },

  /**
   * Track subscription renewal
   */
  renewed: async (planName: string, price: number, currency = 'USD') => {
    await appsflyerService.trackEvent('subscription_renewed', {
      plan_name: planName,
      af_revenue: price,
      af_currency: currency,
    });
  },

  /**
   * Track paywall shown
   */
  paywallShown: async (context: string, trigger: string) => {
    await appsflyerService.trackEvent('paywall_shown', {
      context,
      trigger,
    });
  },
};

/**
 * Track portfolio-related events
 */
export const trackPortfolioEvents = {
  /**
   * Track portfolio creation
   */
  created: async (symbolCount: number, portfolioType = 'custom', name?: string) => {
    await appsflyerService.trackPortfolioCreated(symbolCount, portfolioType);
    if (name) {
      await appsflyerService.trackEvent('portfolio_named', {
        portfolio_type: portfolioType,
        has_custom_name: !!name,
      });
    }
  },

  /**
   * Track portfolio updates
   */
  updated: async (portfolioId: string, changeType: 'add_symbol' | 'remove_symbol' | 'rebalance' | 'rename') => {
    await appsflyerService.trackEvent('portfolio_updated', {
      portfolio_id: portfolioId,
      change_type: changeType,
    });
  },

  /**
   * Track portfolio sharing
   */
  shared: async (portfolioId: string, shareMethod: string) => {
    await appsflyerService.trackEvent('portfolio_shared', {
      portfolio_id: portfolioId,
      share_method: shareMethod,
    });
  },

  /**
   * Track portfolio backtest
   */
  backtested: async (portfolioId: string, timeframe: string) => {
    await appsflyerService.trackEvent('portfolio_backtested', {
      portfolio_id: portfolioId,
      timeframe,
    });
  },
};

/**
 * Track investment research and discovery events
 */
export const trackResearchEvents = {
  /**
   * Track symbol page views
   */
  symbolViewed: async (symbol: string, source: string, viewDuration?: number) => {
    await appsflyerService.trackSymbolView(symbol, source, viewDuration);
  },

  /**
   * Track search usage
   */
  searchPerformed: async (query: string, resultCount: number, source = 'main_search') => {
    await appsflyerService.trackSearch(query, resultCount);
    await appsflyerService.trackEvent('search_context', {
      source,
      has_results: resultCount > 0,
    });
  },

  /**
   * Track AI chat usage
   */
  chatMessageSent: async (messageLength: number, conversationLength: number) => {
    await appsflyerService.trackChatInteraction('user_message');
    await appsflyerService.trackEvent('chat_message_details', {
      message_length: messageLength,
      conversation_length: conversationLength,
    });
  },

  /**
   * Track news article engagement
   */
  newsArticleViewed: async (articleId: string, source: string, symbol?: string) => {
    await appsflyerService.trackEvent('news_article_viewed', {
      article_id: articleId,
      source,
      related_symbol: symbol || 'none',
    });
  },

  /**
   * Track collections/themes viewed
   */
  collectionViewed: async (collectionId: string, collectionName: string) => {
    await appsflyerService.trackEvent('collection_viewed', {
      collection_id: collectionId,
      collection_name: collectionName,
    });
  },
};

/**
 * Track user engagement events
 */
export const trackEngagementEvents = {
  /**
   * Track app session start
   */
  sessionStarted: async (sessionId: string) => {
    await appsflyerService.trackEvent('session_started', {
      session_id: sessionId,
    });
  },

  /**
   * Track feature usage
   */
  featureUsed: async (featureName: string, context?: string) => {
    await appsflyerService.trackEvent('feature_used', {
      feature_name: featureName,
      context: context || 'unknown',
    });
  },

  /**
   * Track notification interactions
   */
  notificationInteracted: async (notificationType: string, action: 'opened' | 'dismissed' | 'tapped') => {
    await appsflyerService.trackEvent('notification_interacted', {
      notification_type: notificationType,
      action,
    });
  },

  /**
   * Track deep link clicks
   */
  deepLinkClicked: async (linkType: string, source: string, destination: string) => {
    await appsflyerService.trackEvent('deep_link_clicked', {
      link_type: linkType,
      source,
      destination,
    });
  },

  /**
   * Track errors or issues
   */
  errorEncountered: async (errorType: string, errorMessage: string, context: string) => {
    await appsflyerService.trackEvent('error_encountered', {
      error_type: errorType,
      error_message: errorMessage.substring(0, 100), // Limit message length
      context,
    });
  },
};

/**
 * Convenience function to track any custom event
 */
export const trackCustomEvent = async (
  eventName: string,
  eventData: Record<string, string | number | boolean> = {},
) => {
  await appsflyerService.trackEvent(eventName, eventData);
};

// Export all tracking utilities
export const appsflyerEvents = {
  onboarding: trackOnboardingEvents,
  subscription: trackSubscriptionEvents,
  portfolio: trackPortfolioEvents,
  research: trackResearchEvents,
  engagement: trackEngagementEvents,
  custom: trackCustomEvent,
};
